import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  Divider,
  Badge,
  Menu,
  MenuItem,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  CheckCircle as ReadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { crmColors } from "../theme/colors";

const NotificationPanel = ({ open, onClose }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: "school",
      title: "New School Registration",
      message: "Greenwood High School has registered for a demo",
      time: "2 minutes ago",
      read: false,
      priority: "high",
    },
    {
      id: 2,
      type: "renewal",
      title: "Renewal Reminder",
      message: "City Central School subscription expires in 5 days",
      time: "1 hour ago",
      read: false,
      priority: "medium",
    },
    {
      id: 3,
      type: "order",
      title: "New Order Received",
      message: "Order ORD-2024-005 has been placed by Riverside School",
      time: "3 hours ago",
      read: true,
      priority: "low",
    },
    {
      id: 4,
      type: "training",
      title: "Training Scheduled",
      message: "Training session scheduled for Sunshine Academy tomorrow",
      time: "5 hours ago",
      read: true,
      priority: "low",
    },
    {
      id: 5,
      type: "warning",
      title: "Payment Pending",
      message: "Payment pending for Mountain View School order",
      time: "1 day ago",
      read: false,
      priority: "high",
    },
  ]);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case "school":
        return <SchoolIcon />;
      case "renewal":
        return <WarningIcon />;
      case "order":
        return <SuccessIcon />;
      case "training":
        return <InfoIcon />;
      case "warning":
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === "high") return theme.palette.error.main;
    if (priority === "medium") return theme.palette.warning.main;
    return theme.palette.info.main;
  };

  const handleMarkAsRead = (id) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const handleDelete = (id) => {
    setNotifications(notifications.filter((n) => n.id !== id));
    setMenuAnchor(null);
  };

  const handleDeleteAll = () => {
    setNotifications([]);
    setMenuAnchor(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400 },
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, ${crmColors.hero.start} 0%, ${crmColors.hero.mid} 50%, ${crmColors.hero.end} 100%)`,
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: "white" }}
            >
              <MoreVertIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={handleMarkAllAsRead}>
            <ReadIcon sx={{ mr: 1 }} fontSize="small" />
            Mark all as read
          </MenuItem>
          <MenuItem onClick={handleDeleteAll}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete all
          </MenuItem>
        </Menu>

        {/* Notifications List */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <NotificationsIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              <AnimatePresence>
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <ListItem
                      sx={{
                        bgcolor: notification.read ? "transparent" : alpha(theme.palette.primary.main, 0.05),
                        borderLeft: notification.read
                          ? "none"
                          : `3px solid ${getNotificationColor(notification.type, notification.priority)}`,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: alpha(
                              getNotificationColor(notification.type, notification.priority),
                              0.1
                            ),
                            color: getNotificationColor(notification.type, notification.priority),
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: notification.read ? 500 : 600,
                                flex: 1,
                              }}
                            >
                              {notification.title}
                            </Typography>
                            {notification.priority === "high" && (
                              <Chip
                                label="High"
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: "0.65rem" }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                mb: 0.5,
                                display: "block",
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {notification.time}
                            </Typography>
                          </>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNotification(notification.id);
                          handleDelete(notification.id);
                        }}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                    <Divider />
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark all read
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              color="error"
              onClick={handleDeleteAll}
            >
              Clear all
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default NotificationPanel;

